import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  Loader2,
  MapPin,
  MessageCircle,
  Phone,
  Users,
} from "lucide-react";
import { ScreenHeader, Card } from "./MobileShell";
import AICoachCard from "./AICoachCard";
import { clientListSessions, clientRsvp, clientCancelRsvp } from "../lib/api";

const COACH = {
  name: "Arvind Arora",
  title: "Longevity Coach · Founder, LONGEV CIRCLE",
  photo:
    "/brand/founder.jpg",
  whatsapp: "https://wa.me/41795551234?text=Hi%20Arvind%2C%20from%20the%20app%20—%20",
  bookingUrl: "/#book",
};

export default function CoachScreen() {
  const { token } = useOutletContext();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const d = await clientListSessions(token);
      setSessions(Array.isArray(d) ? d.filter((s) => s && s.id) : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRsvp = async (s) => {
    setBusyId(s.id);
    try {
      if (s.is_rsvped || s.is_waitlisted) await clientCancelRsvp(token, s.id);
      else await clientRsvp(token, s.id);
    } catch {
      /* ignore */
    } finally {
      await load();
      setBusyId(null);
    }
  };

  return (
    <>
      <ScreenHeader eyebrow="Coach" title="Arvind Arora" />

      <Card className="mb-4 overflow-hidden !p-0" data-testid="coach-card">
        <div className="relative aspect-[4/3] bg-[var(--lc-cream)]">
          <img
            src={COACH.photo}
            alt="Coach Arvind Arora"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="p-4">
          <div className="font-serif-display text-xl leading-tight">{COACH.name}</div>
          <div className="text-xs text-[var(--lc-muted)] mt-1">{COACH.title}</div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <a
              href={COACH.whatsapp}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#25D366] text-white text-[11px] font-bold tracking-[0.14em] uppercase rounded-md"
              data-testid="coach-whatsapp"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            <a
              href={COACH.bookingUrl}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[11px] font-bold tracking-[0.14em] uppercase rounded-md"
              data-testid="coach-book"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Book session
            </a>
          </div>
          <a
            href={COACH.whatsapp}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase rounded-md"
            data-testid="coach-message"
          >
            <Phone className="h-3.5 w-3.5" /> Message Coach
          </a>
        </div>
      </Card>

      <AICoachCard token={token} />

      <div className="mb-2 eyebrow text-[var(--lc-muted)]">Upcoming sessions</div>
      {loading ? (
        <div className="py-10 grid place-items-center">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-muted)]" />
        </div>
      ) : sessions.length === 0 ? (
        <Card data-testid="coach-no-sessions">
          <div className="text-sm text-[var(--lc-muted)]">
            Nothing scheduled right now. Message Arvind to book your next 1:1 or group session.
          </div>
        </Card>
      ) : (
        <ul className="space-y-3" data-testid="coach-sessions">
          {sessions.map((s) => {
            const full = s.seats_left === 0;
            const isGroup = s.type === "group";
            let label = "RSVP";
            let cls = "bg-[var(--lc-ink)] text-white";
            if (s.is_rsvped) {
              label = "You're in";
              cls = "bg-[var(--lc-moss)] text-white";
            } else if (s.is_waitlisted) {
              label = "Waitlisted";
              cls = "bg-amber-500 text-white";
            } else if (full && isGroup) {
              label = "Join waitlist";
              cls = "bg-amber-500 text-white";
            } else if (full) {
              label = "Full";
              cls = "bg-[var(--lc-line)] text-[var(--lc-muted)]";
            }
            return (
              <li key={s.id}>
                <Card data-testid={`coach-session-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-serif-display text-lg leading-tight">{s.title}</div>
                      <div className="mt-1 text-xs text-[var(--lc-muted)]">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" />
                          {new Date(s.start_at).toLocaleString("en-GB", {
                            weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--lc-muted)]">
                        {s.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {s.location}
                          </span>
                        )}
                        {isGroup && (
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {s.confirmed_count}/{s.capacity}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      disabled={busyId === s.id || (full && !isGroup && !s.is_rsvped)}
                      onClick={() => toggleRsvp(s)}
                      className={`px-3 py-1.5 text-[10px] font-bold tracking-[0.14em] uppercase rounded-md disabled:opacity-50 ${cls}`}
                      data-testid={`coach-session-rsvp-${s.id}`}
                    >
                      {busyId === s.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : s.is_rsvped ? (
                        <CheckCircle2 className="h-3 w-3 inline" />
                      ) : null}{" "}
                      {label}
                    </button>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
